import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Eyebrow colours. `inherit` takes the surrounding text colour (navy on light, sheet on
 * navy). On light surfaces: `muted` (5.62:1 on paper) or `rust` (5.72:1). On navy:
 * `peach` (9.89:1) or `sheet` (sheet at 80%, 10.4:1).
 */
export const EYEBROW_TONES = {
  inherit: '',
  muted: 'text-brand-muted',
  rust: 'text-brand-rust',
  peach: 'text-brand-peach',
  sheet: 'text-brand-sheet/80',
};

/** "1" → "01"; strings are kept as they are. */
export function formatIndex(index) {
  if (typeof index === 'number' && Number.isFinite(index)) return String(index).padStart(2, '0');
  return String(index);
}

/**
 * A short IBM Plex Mono label above a heading ("01 / ON THE TABLE"). Write the text in
 * sentence case: it is uppercased by CSS, so screen readers read words, not letters.
 *
 * Props:
 * - `index`: optional number or string shown before a slash (numbers are zero-padded:
 *   1 → "01"); the slash is hidden from screen readers
 * - `tone`: 'inherit' (default) | 'muted' | 'rust' | 'peach' | 'sheet'
 * - `icon`: optional decorative icon node (e.g. a lucide icon), 16px, before the text
 * - `as`: element (default 'span'; use 'p' when it stands alone as a line)
 * - `className`, `children`, other props pass through.
 */
export default function Eyebrow({ index, tone = 'inherit', icon, as: Tag = 'span', className, children, ...rest }) {
  const hasIndex = index !== undefined && index !== null && index !== '';
  return (
    <Tag
      className={cn(
        'inline-flex max-w-full flex-wrap items-center gap-x-[7px] font-label text-xs uppercase leading-[1.65] tracking-[0.07em]',
        EYEBROW_TONES[tone] ?? '',
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span aria-hidden="true" className="inline-flex shrink-0 [&_svg]:size-4">
          {icon}
        </span>
      ) : null}
      {hasIndex ? (
        <>
          <span>{formatIndex(index)}</span>
          <span aria-hidden="true">/</span>
        </>
      ) : null}
      <span>{children}</span>
    </Tag>
  );
}
